import "maplibre-gl/dist/maplibre-gl.css";
import { useLiveQuery } from "dexie-react-hooks";
import { distance } from "@turf/turf";
import { useGeolocation } from "../util/useGeolocation";
import { ArtBoxCard } from "../components/ArtBox";
import { db, type Box } from "../db";
import { Link } from "wouter";
import { useMemo } from "react";

export function Nearby() {
  const { position, error, request } = useGeolocation();

  // only the most recent boxes
  const newest = useLiveQuery(async () => {
    const all = await db.boxes.orderBy("[lon+lat]").toArray();

    let prev: Box | undefined = undefined;

    const keep = new Set<Box>();

    for (const box of all) {
      keep.add(box);

      // matches the previous box
      if (prev && prev.lat === box.lat && prev.lon === box.lon) {
        if (prev.year < box.year) {
          // replace the box
          keep.delete(prev);
        } else {
          // don't add this box
          keep.delete(box);
        }
      }

      prev = box;
    }

    return all.filter((b) => keep.has(b));
  }, []);

  const nearby = useMemo(() => {
    if (!position || !newest) return [];

    const boxes = newest.slice(0).sort((a, b) => {
      const dA = distance(
        [a.lon, a.lat],
        [position.coords.longitude, position.coords.latitude]
      );
      const dB = distance(
        [b.lon, b.lat],
        [position.coords.longitude, position.coords.latitude]
      );

      return dA - dB;
    });

    return boxes.slice(0, 10);
  }, [position, newest]);

  return (
    <>
      <h1>
        <Link to="/">&larr;</Link> Nearby Artworks
      </h1>

      <header>
        {!position && <button onClick={request}>Request location</button>}
        {error && <div>Error: {error.message}</div>}
      </header>

      <main>
        {nearby.map((box) => (
          <ArtBoxCard key={box.id} id={box.id} position={position} />
        ))}
      </main>
    </>
  );
}
